import { ALL_M, parseLocalDate } from './peopleMonthly';

/* Moteur du PLAN d'effectif — les fonctions pures que la Planification et la
   Synthèse des effectifs partagent (2026-08-29).

   Elles vivaient dans `HrPlanningPage.js`. La synthèse en a besoin pour dire
   « où je vais » : sans elles, elle aurait recalculé sa propre projection, et
   deux moteurs auraient fini par répondre deux chiffres différents à la même
   question. Le déplacement est PUR : aucun comportement n'a changé.

   Vocabulaire : une CELLULE = un mois d'une personne, `{ kind, pct, gross }`.
   `pct` est le taux d'activité en pour-cent, `gross` le brut mensuel. */

export function monthStartOf(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

export function contractActiveInMonth(c, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd   = new Date(year, monthIdx + 1, 0);
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}

export function pickReferenceContract(contracts) {
  if (!contracts || contracts.length === 0) return null;
  const open = contracts.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : contracts;
  return pool.reduce((best, c) => (!best || (c.start_date || '') > (best.start_date || '') ? c : best), null);
}

export function pickContractForMonth(contracts, year, monthIdx) {
  const active = contracts.filter(c => contractActiveInMonth(c, year, monthIdx));
  if (active.length === 0) return null;
  const open = active.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : active;
  return pool.reduce((best, c) => (!best || (c.start_date || '') > (best.start_date || '') ? c : best), null);
}

// Cellules contrat (baseline) d'une personne — identique à Year-end.
export function buildBaseCells(person, contracts, year) {
  const entry = person.entry_date ? parseLocalDate(person.entry_date) : null;
  const term  = person.termination_date ? parseLocalDate(person.termination_date) : null;
  return ALL_M.map(m => {
    const monthStart = new Date(year, m, 1);
    const monthEnd   = new Date(year, m + 1, 0);
    if (entry && entry > monthEnd)  return { kind: 'before', pct: 0 };
    if (term  && term  < monthStart) return { kind: 'after',  pct: 0 };
    const c = pickContractForMonth(contracts, year, m);
    if (!c) return { kind: 'none', pct: 0 };
    return { kind: 'active', pct: Math.round(Number(c.activity_rate || 0) * 100) };
  });
}

// Applique les mouvements rattachés à une personne sur ses cellules baseline.
// rate_change → taux absolu (fte) sur [start, end] ; departure → 0 dès start.
// Les overlays ne s'appliquent qu'aux mois OUVERTS (après clôture) : le passé
// reste le réel des contrats.
export function applyPersonMovements(baseCells, salary100, personMovements, year, isOpen) {
  return ALL_M.map(m => {
    const base = baseCells[m];
    let kind = base.kind;
    let rate = base.kind === 'active' ? base.pct / 100 : 0;
    const cell = new Date(year, m, 1);
    // Les overlays ne s'appliquent qu'aux mois réellement sous contrat
    // (kind 'active') et ouverts : on ne ressuscite pas une personne avant son
    // entrée / après sa sortie, ni on ne planifie un mois déjà clôturé.
    if (base.kind === 'active' && isOpen(m)) {
      for (const mv of personMovements) {
        if (mv.movement_type !== 'rate_change') continue;
        const sd = monthStartOf(parseLocalDate(mv.start_month));
        const ed = mv.end_month ? monthStartOf(parseLocalDate(mv.end_month)) : null;
        if (cell >= sd && (!ed || cell <= ed)) { rate = Number(mv.fte || 0); kind = 'rate'; }
      }
      for (const mv of personMovements) {
        if (mv.movement_type !== 'departure') continue;
        const sd = monthStartOf(parseLocalDate(mv.start_month));
        if (cell >= sd) { rate = 0; kind = 'exit'; }
      }
    }
    // Modification de salaire : un AJOUT (delta signé, +/-) au brut calculé —
    // pas une nouvelle référence 100 %. Simple, ne recoupe pas le calcul du
    // taux ; s'applique même sur un mois « exit » (délai à verser) mais pas
    // sur un mois hors contrat/fermé. Plusieurs deltas actifs se cumulent.
    let salaryDelta = 0;
    if (base.kind === 'active' && isOpen(m)) {
      for (const mv of personMovements) {
        if (mv.movement_type !== 'salary_change') continue;
        const sd = monthStartOf(parseLocalDate(mv.start_month));
        const ed = mv.end_month ? monthStartOf(parseLocalDate(mv.end_month)) : null;
        if (cell >= sd && (!ed || cell <= ed)) salaryDelta += Number(mv.monthly_amount || 0);
      }
    }
    return { kind, pct: Math.round(rate * 100), gross: salary100 * rate + salaryDelta };
  });
}

// Ligne synthétique (mouvement sans person_id) : hire (+) ou departure (−).
export function buildSyntheticRow(mv, year, isOpen) {
  const sign = mv.movement_type === 'departure' ? -1 : 1;
  const fte = Number(mv.fte != null ? mv.fte : 1) || 0;
  const amount = Number(mv.monthly_amount || 0);
  const salary100 = fte > 0 ? amount / fte : amount;
  const cells = ALL_M.map(m => {
    const cell = new Date(year, m, 1);
    const sd = monthStartOf(parseLocalDate(mv.start_month));
    const ed = mv.end_month ? monthStartOf(parseLocalDate(mv.end_month)) : null;
    const active = isOpen(m) && cell >= sd && (!ed || cell <= ed);
    return {
      kind: sign > 0 ? 'hire' : 'cut',
      pct: active ? Math.round(fte * 100) * sign : 0,
      gross: active ? amount * sign : 0,
    };
  });
  return { id: mv.id, synthetic: true, mv, sign, label: mv.label,
           department: mv.department || '', salary100, cells };
}

// Type « logique » exposé à l'UI (départ nommé vs réduction anonyme).
export function logicalType(mv) {
  if (mv.movement_type === 'hire') return 'hire';
  if (mv.movement_type === 'rate_change') return 'rate_change';
  if (mv.movement_type === 'salary_change') return 'salary_change';
  if (mv.movement_type === 'departure') return mv.person_id ? 'exit' : 'cut';
  return mv.movement_type;
}
